<template>
    <div>
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.driver_add)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="openAddBankAccount">
                {{ $t('labels.addBankAccount') }}
            </hts-form-button>
        </div>

        <hts-data-table
            ref="dataTables"
            :headers="driverHeaders"
            :server="apiListBankAccount"
            use-selection
            :use-archive="useBankAccountArchive"
            store-name="bankAccountDataTable"
            store-key="BankAccount" />

        <AddBankAccount
            v-if="checkPermission(layoutPermissions.driver_add)"
            ref="addBankAccount"
            @refresh-tables="refreshTables()" />

        <EditBankAccount
            v-if="checkPermission(layoutPermissions.driver_edit)"
            ref="editBankAccount"
            @refresh-tables="refreshTables()" />

        <DeleteBankAccount
            v-if="checkPermission(layoutPermissions.driver_edit)"
            ref="deleteBankAccount"
            @refresh-tables="refreshTables()" />
    </div>
</template>

<script lang="ts" setup>
import AddBankAccount from './ignore/add.vue'
import DeleteBankAccount from './ignore/delete.vue'
import EditBankAccount from './ignore/edit.vue'
import Actions from '@/pages/trucker-[id]/vehicle/ignore/actions.vue'

import layoutPermissions from '@/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'

const { $formatters } = useNuxtApp()

const dataTables = ref()
const addBankAccount = ref()
const editBankAccount = ref()
const deleteBankAccount = ref()

const apiListBankAccount = $url.settings.listBankAccount

const openAddBankAccount = () => {
  if (addBankAccount?.value) {
    addBankAccount.value.open()
  }
}

const openEdit = (bankAccount: any) => {
  if (!bankAccount) {
    return null
  }

  editBankAccount.value?.open(bankAccount._id)
}

const openDelete = (bankAccount: any) => {
  if (!bankAccount) {
    return null
  }

  deleteBankAccount.value?.open(bankAccount._id)
}

const useBankAccountArchive = { api: $url.settings.archiveBankAccount, permissions: checkPermission(layoutPermissions.driver_archive) }

const driverHeaders = [
  {
    field: 'bankName',
    headerName: $t('labels.bankName'),
    resizable: true,
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'accountNumber',
    headerName: $t('labels.accountNumber'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'accountName',
    headerName: $t('labels.accountName'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'swiftCode',
    headerName: $t('labels.swiftCode'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'branch',
    headerName: $t('labels.branch'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'bankAddress',
    headerName: $t('labels.bankAddress'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      if (params.data.createdAt) {
        return $formatters.datetime(params.data.createdAt)
      }
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      if (params.data.updatedAt) {
        return $formatters.datetime(params.data.updatedAt)
      }
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'bankAccountDataTable',
      storeName: 'BankAccount',
      items: [
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.edit'),
          class: 'text-main-600 hover:text-main-500',
          permissions: checkPermission(layoutPermissions.driver_edit),
          click: openEdit
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-500 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.driver_delete),
          click: openDelete,
          isArchive: true
        }
      ]
    }
  }
]

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

function formatDate (date: Date) {
  const d = new Date(date)
  let month = '' + (d.getMonth() + 1)
  let day = '' + d.getDate()
  const year = d.getFullYear()

  if (month.length < 2) {
    month = '0' + month
  }
  if (day.length < 2) {
    day = '0' + day
  }

  return [year, month, day].join('-')
}
</script>
